/**
 * TokenUsageDialog Component
 * Dialog for viewing automation credit/token usage breakdown
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  useTheme,
  LinearProgress,
  Button
} from '@mui/material';
import {
  CloseOutlined as CloseIcon,
  AutoAwesomeOutlined as AutoIcon,
  ArrowForwardOutlined as ArrowIcon
} from '@mui/icons-material';

interface TokenUsageDialogProps {
  open: boolean;
  onClose: () => void;
  usedTokens: number;
  totalTokens: number;
  onUpgrade?: () => void;
}

export const TokenUsageDialog: React.FC<TokenUsageDialogProps> = ({
  open,
  onClose,
  usedTokens,
  totalTokens,
  onUpgrade
}) => {
  const theme = useTheme();
  
  // Next plan tier credits (mock data - should come from plan data)
  const nextPlanCredits = 20000;

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const usagePercentage = (usedTokens / totalTokens) * 100;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.6)'
            : '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2.5,
          px: 3,
          pt: 2.5,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '16px' }}>
          AI credits usage
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ borderRadius: '6px', color: 'text.secondary', ml: 2 }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3.5, pb: 3 }}>
        {/* Overview */}
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', mb: 2.5 }}>
            View your AI credit usage and allocation
          </Typography>
          
          {/* Total Usage Bar */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '14px' }}>
                {formatNumber(usedTokens)} of {formatNumber(totalTokens)} credits used
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                {usagePercentage.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={usagePercentage}
              sx={{
                height: 8,
                borderRadius: 1,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 1,
                  backgroundColor: theme.palette.warning.main
                }
              }}
            />
          </Box>

          {/* Additional Info */}
          <Box
            sx={{
              p: 2,
              borderRadius: '6px',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <AutoIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500} sx={{ fontSize: '13px', mb: 0.5 }}>
                  Need more credits?
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', lineHeight: 1.5, display: 'block', mb: 1.5 }}>
                  AI credits reset monthly based on your plan. Upgrade to get more credits for AI-powered features.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px', display: 'block' }}>
                      Next plan includes
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '13px' }}>
                      {formatNumber(nextPlanCredits)} credits/month
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<ArrowIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      onUpgrade?.();
                      onClose();
                    }}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '6px',
                      px: 2,
                      py: 0.75,
                      backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
                      }
                    }}
                  >
                    Upgrade
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

